<script setup lang="ts">
import Glide from '@glidejs/glide';
import { useStorage } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AdsBanner from '@/components/announcements/ads/ads-banner.vue';
import ShipBanner from '@/components/announcements/ship/ship-banner.vue';
import '@glidejs/glide/dist/css/glide.core.min.css';

const features = useFeature(['ads_banner', 'ship_banner']);

const banners = [
  { id: 'ship_banner', component: ShipBanner, feature: features.ship_banner },
  { id: 'ads_banner', component: AdsBanner, feature: features.ads_banner },
];

const hiddenTimestamps = useStorage<Record<string, number | null>>('banners.hidden_timestamps', {});
const ONE_HOUR = 60 * 60 * 1000;
const glide = ref<Glide | null>(null);

function isHidden(id: string) {
  const t = hiddenTimestamps.value?.[id];
  return Boolean(t && Date.now() - t < ONE_HOUR);
}

function closeBanner(id: string) {
  hiddenTimestamps.value = { ...hiddenTimestamps.value, [id]: Date.now() };
}

const visibleBanners = computed(() =>
  banners.filter((b) => {
    const active = (b.feature && (b.feature?.active ?? b.feature)) ?? false;
    return active && !isHidden(b.id);
  }),
);

function mountGlide() {
  if (glide.value)
    return;

  nextTick(() => {
    if (visibleBanners.value.length <= 1)
      return;

    glide.value = new Glide('.glide', {
      type: 'carousel',
      perView: 1,
      gap: 20,
      autoplay: 7000,
      hoverpause: true,
    });

    glide.value.mount();
  });
}

function destroyGlide() {
  if (glide.value) {
    try {
      glide.value.destroy();
    }
    finally {
      glide.value = null;
    }
  }
}

watch(
  visibleBanners,
  (newVal, oldVal) => {
    const newLen = newVal.length;
    const oldLen = oldVal?.length ?? 0;

    if (newLen <= 1)
      return destroyGlide();
    if (oldLen <= 1 && newLen > 1)
      return mountGlide();

    if (oldLen > 1 && newLen > 1) {
      destroyGlide();
      mountGlide();
    }
  },
  { immediate: true },
);

onMounted(() => mountGlide());
onBeforeUnmount(() => destroyGlide());
</script>

<template>
  <div v-if="visibleBanners.length > 0">
    <div class="glide" dir="ltr">
      <div class="glide__track" data-glide-el="track">
        <ul class="glide__slides">
          <li
            v-for="banner in visibleBanners"
            :key="banner.id"
            class="glide__slide"
          >
            <component
              :is="banner.component"
              class="banner"
              @close="closeBanner(banner.id)"
            />
          </li>
        </ul>
      </div>

      <div
        v-if="visibleBanners.length > 1"
        class="glide__bullets"
        data-glide-el="controls[nav]"
      >
        <button
          v-for="(banner, i) in visibleBanners"
          :key="banner.id"
          class="glide__bullet"
          :data-glide-dir="`=${i}`"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
[dir='rtl'] .banner {
  &,
  * {
    direction: rtl;
    text-align: initial;
  }
}
</style>

<style scoped>
/* stylelint-disable selector-class-pattern */
.glide :deep(.glide__slide) {
  height: auto;

  & > div {
    height: 100%;
  }
}

.glide__bullets {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-8);
  margin-top: 12px;
}

.glide__bullet {
  width: var(--spacing-8);
  height: var(--spacing-8);
  transition: all 500ms;
  border-radius: 50px;
  background: var(--color-neutral-200);
}

.glide__bullet--active {
  background-color: var(--color-neutral-600);
}
</style>
